import type { Undefinable } from "@types";
import { getJobInfo } from "api/@common/api_common";
import moment from "moment";
import toast from "react-hot-toast";

export const objectToQueryString = (optionObject: Record<any, any>) => {
  const cleanedFilterOptions: Record<string, string> = {};
  Object.entries(optionObject).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      cleanedFilterOptions[key] = value;
    }
  });
  const query = new URLSearchParams(cleanedFilterOptions).toString();
  return query;
};

export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
};

export const throttle = (callback: () => void, delay: number) => {
  let isThrottled = false;
  return () => {
    if (isThrottled) return;
    callback();
    isThrottled = true;
    setTimeout(() => {
      isThrottled = false;
    }, delay);
  };
};

export const formattingDate = (date: Date) => {
  const differenceMillis = new Date().getTime() - new Date(date).getTime();
  const differenceDays = differenceMillis / (1000 * 60 * 60 * 24);
  if (differenceDays < 1) {
    const differenceHours = differenceMillis / (1000 * 60 * 60);
    if (differenceHours < 1) {
      return "방금 전";
    }
    return `${Math.floor(differenceHours)}시간 전`;
  }
  return moment(date).format("YY.MM.DD");
};

/** e.g. 1994-09-23 */
export const formatDateYYYYMMDD = (date: string | Date): Undefinable<string> => {
  const notDateType = isNaN(Date.parse(date.toString()));
  if (notDateType) return undefined;

  const year = new Date(date).getFullYear().toString();
  const month = (new Date(date).getMonth() + 1).toString().padStart(2, "0");
  const day = new Date(date).getDate().toString().padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/** e.g. 1994.09.23 */
export const formatDateDotsYYYYMMDD = (date: string | Date): Undefinable<string> => {
  const notDateType = isNaN(Date.parse(date.toString()));
  if (notDateType) return undefined;

  const year = new Date(date).getFullYear().toString();
  const month = (new Date(date).getMonth() + 1).toString().padStart(2, "0");
  const day = new Date(date).getDate().toString().padStart(2, "0");

  return `${year}.${month}.${day}`;
};

/** 오늘로부터의 차이 표시 */
export function calculateTimePassed(date: Date) {
  const now = new Date();
  const diffInMilliseconds = now.getTime() - date.getTime();

  // Calculate time differences
  const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);

  if (diffInHours < 24) {
    if (diffInHours < 1) {
      return "방금 전";
    }
    return `${Math.floor(diffInHours)}시간 전`;
  }

  // If more than a day but less than a week
  if (diffInHours >= 24 && diffInHours < 168) {
    return `${Math.round(diffInHours / 24)}일 전`;
  }

  // If more than a week but less than a month
  if (diffInHours >= 168 && diffInHours < 730) {
    return `${Math.round(diffInHours / 168)}주 전`;
  }

  // If more than a month but less than a year
  if (diffInHours >= 730 && diffInHours < 8760) {
    return `${Math.round(diffInHours / 730)}달 전`;
  }

  // If more than a year
  return `${Math.round(diffInHours / 8760)}년 전`;
}

export const copyClipboard = (message: string, toastMessage?: string) => {
  toastMessage && toast.success(toastMessage);
  window.navigator.clipboard.writeText(message);
};

export const convertDateToFull = (yymmdd: string) => {
  const d = yymmdd; // YYMMDD
  const yy = d.substr(0, 2);
  const mm = d.substr(2, 2);
  const dd = d.substr(4, 2);
  const yyyy = +yy < 90 ? "20" + yy : "19" + yy;
  return yyyy + "-" + mm + "-" + dd;
};

export const base64ToImageUrl = (base64: string) => {
  const decode = window.atob(base64!.split(",")[1]); // Base64 디코딩
  const arraybuffer = []; // Base64 -> ArrayBuffer  Blob으로 바꾸는 중간과정에서 arrayBuffer가 쓰임
  for (let i = 0; i < decode.length; i++) {
    arraybuffer.push(decode.charCodeAt(i) & 0xff);
  }

  const byteArray = new Uint8Array(arraybuffer);
  const blob = new Blob([byteArray], {
    // ArrayBuffer -> Blob
    type: "image/png",
  });

  // Blob 객체를 URL.createObjectURL()을 사용하여 이미지 URL로 변환
  const imageUrl = URL.createObjectURL(blob);

  return imageUrl;
};

type TIntervalJobParams = {
  jobId: string;
  userId: string;
  successAction: (analysisId: number | null) => void;
  handleFailure: () => void;
};

export const intervalJob = ({ userId, jobId, successAction, handleFailure }: TIntervalJobParams) => {
  const timerId = setTimeout(() => {
    clearInterval(jobInterval);
    clearTimeout(timerId);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    handleFailure;
  }, 40 * 1000);

  const jobInterval = setInterval(async () => {
    try {
      const jobInfo = await getJobInfo({ jobId, userId });

      if (jobInfo.jobInfo.status === "COMPLETE") {
        clearInterval(jobInterval);
        clearTimeout(timerId);
        successAction(jobInfo.jobInfo.foreheadAnalysisId || jobInfo.jobInfo.scalpAnalysisId);
      }

      if (jobInfo.jobInfo.status === "ERROR") {
        clearInterval(jobInterval);
        clearTimeout(timerId);
        handleFailure();
      }
    } catch {
      clearInterval(jobInterval);
      clearTimeout(timerId);
      handleFailure();
    }
  }, 2000);
};

export const base64ToImageFile = async (base64Img: string) => {
  const blob = await fetch(base64Img).then((res) => res.blob());
  const file = new File([blob], "simulationImage.png", { type: "image/png", lastModified: Date.now() });
  return file;
};

export const dayOfWeek = (date: Date) => {
  const daysOfWeek = ["일요일", "월요일", "화요일", "수요일", "목요일", "금요일", "토요일"];
  const dayOfWeek = daysOfWeek[new Date(date).getDay()];

  return dayOfWeek;
};
export const getYearMonthDayTime = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  let hours = date.getHours();
  let minutes = date.getMinutes();

  hours = Number(hours < 10 ? `0${hours}` : `${hours}`);
  minutes = Number(minutes < 10 ? `0${minutes}` : `${minutes}`);

  const formattedTime = `${hours}:${minutes}`;
  const formattedDateAndTime = `${year}.${month}.${day} ${formattedTime}`;
  return formattedDateAndTime;
};

export const formatToKoreanTime = (isoString: string) => {
  const date = new Date(isoString);
  const options = {
    timeZone: "Asia/Seoul",
  };
  return new Intl.DateTimeFormat("ko-KR", options).format(date);
};
